/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { DefaultSolutionView } from '@modules/deployment-creation-wizard/types'
import type { EbtHookReturnType } from '@modules/ebt/hook'
import useEbt from '@modules/ebt/hook'
import type { UseCaseSolutionPickerVersion } from '@modules/solution-selector/types'

const DEPLOYMENT_USE_CASE = 'deployment_use_case' as const
const DEPLOYMENT_RESOURCE = 'deployment_resource' as const
const DEPLOYMENT_KIBANA = 'deployment_kibana' as const

const EVENT_TYPES = [DEPLOYMENT_USE_CASE, DEPLOYMENT_RESOURCE, DEPLOYMENT_KIBANA] as const

type EventType = (typeof EVENT_TYPES)[number]

type DeploymentPayloadSchema = {
  eventType: typeof DEPLOYMENT_USE_CASE
  payload: {
    answer: DefaultSolutionView
    resource: 'stateful'
    solutionVersion: UseCaseSolutionPickerVersion
  }
}

type DeploymentResourcePayloadSchema = {
  eventType: typeof DEPLOYMENT_RESOURCE
  payload: {
    answer: {
      region: string
      version: string
      platform: string
      hardware: string
    }
    resource: 'stateful'
  }
}

type DeploymentKibanaPayloadSchema = {
  eventType: typeof DEPLOYMENT_KIBANA
  payload: {
    answer: boolean
    resource: 'stateful'
  }
}

type EventPayloadSchema =
  | DeploymentPayloadSchema
  | DeploymentResourcePayloadSchema
  | DeploymentKibanaPayloadSchema

const useEbtDeploymentCreationWizard = (): EbtHookReturnType<EventType, EventPayloadSchema> => {
  const reportEbtEvent = useEbt<EventPayloadSchema>(
    {
      eventType: DEPLOYMENT_USE_CASE,
      schemaProps: {
        answer: {
          keywordType: 'keyword',
          description: 'The default solution view chosen by the user before creating a deployment',
        },
        resource: {
          keywordType: 'keyword',
          description: 'The type of resource the event is related to (stateful)',
        },
        solutionVersion: {
          keywordType: 'keyword',
          description: 'The version of the solution picker used',
        },
      },
    },
    {
      eventType: DEPLOYMENT_RESOURCE,
      schemaProps: {
        answer: {
          keywordType: 'pass_through',
          description: 'The selected cloud provider, region, hardware, and version',
        },
        resource: {
          keywordType: 'keyword',
          description: 'The type of resource the event is related to (stateful)',
        },
      },
    },
    {
      eventType: DEPLOYMENT_KIBANA,
      schemaProps: {
        answer: {
          keywordType: 'boolean',
          description: 'Whether the user opened Kibana during the deployment creation process',
        },
        resource: {
          keywordType: 'keyword',
          description: 'The type of resource the event is related to (stateful)',
        },
      },
    },
  )

  const resource = 'stateful'

  return {
    reportDeploymentUseCaseEbt: ({ answer, solutionVersion }) => {
      reportEbtEvent(DEPLOYMENT_USE_CASE, {
        answer,
        resource,
        solutionVersion,
      })
    },
    reportDeploymentResourceEbt: ({ answer }) => {
      reportEbtEvent(DEPLOYMENT_RESOURCE, {
        answer,
        resource,
      })
    },
    reportDeploymentKibanaEbt: () => {
      reportEbtEvent(DEPLOYMENT_KIBANA, {
        answer: true,
        resource,
      })
    },
  }
}

export default useEbtDeploymentCreationWizard
